import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Wine, Loader2, Minus, Plus, Coins } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

export const Route = createFileRoute("/menu")({
  head: () => ({ meta: [{ title: "İçecek Menüsü — JUËN" }] }),
  component: MenuPage,
});

interface DrinkRow {
  id: string;
  name: string;
  description: string | null;
  price_kurus: number;
  abv_percent: number;
  volume_ml: number;
}




function MenuPage() {
  const { user, profile, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [drinks, setDrinks] = useState<DrinkRow[]>([]);
  const [drinkQty, setDrinkQty] = useState<Record<string, number>>({});
  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const tc = useAutoTranslate(drinks.flatMap((d) => [d.name, d.description]));

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user || profile?.membership_status !== "approved") return;
    void (async () => {
      const { data: dr } = await supabase
        .from("drinks")
        .select("id,name,description,price_kurus,abv_percent,volume_ml")
        .is("event_id", null)
        .eq("is_active", true)
        .order("sort_order");
      setDrinks((dr as DrinkRow[]) ?? []);
      setPageLoading(false);
    })();
  }, [user, profile?.membership_status]);


  const totalKurus = useMemo(
    () => drinks.reduce((sum, d) => sum + (drinkQty[d.id] ?? 0) * d.price_kurus, 0),
    [drinks, drinkQty],
  );

  const totalItems = useMemo(
    () => Object.values(drinkQty).reduce((s, n) => s + n, 0),
    [drinkQty],
  );

  const pointsNeeded = Math.ceil(totalKurus / 100);
  const balance = profile?.points_balance ?? 0;

  const setQty = (drinkId: string, delta: number) => {
    setDrinkQty((prev) => {
      const next = Math.max(0, (prev[drinkId] ?? 0) + delta);
      return { ...prev, [drinkId]: next };
    });
  };

  const handleCheckout = async () => {
    if (totalItems === 0) {
      toast.error(t("account.menu.selectAtLeastOne"));
      return;
    }
    if (balance < pointsNeeded) {
      toast.error(t("account.menu.insufficientPoints", { needed: pointsNeeded, balance }));
      return;
    }
    setSubmitting(true);
    try {
      const drinkPayload = drinks
        .filter((d) => (drinkQty[d.id] ?? 0) > 0)
        .map((d) => ({ drink_id: d.id, quantity: drinkQty[d.id] }));

      const { data: createRes, error: createErr } = await supabase.rpc(
        "create_drinks_order",
        { _event_id: null as unknown as string, _drinks: drinkPayload },
      );

      if (createErr) throw createErr;
      const orderId = (createRes as { order_id?: string } | null)?.order_id;
      if (!orderId) throw new Error(t("account.menu.orderFailed"));

      const { error: payErr } = await supabase.rpc(
        "pay_order_with_points_drinks",
        { _order_id: orderId },
      );
      if (payErr) throw payErr;

      await refreshProfile();
      setDrinkQty({});
      toast.success(t("account.menu.ready"));
      void navigate({ to: "/vouchers" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.error");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  if (profile?.membership_status !== "approved") {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{t("account.menu.approvalRequiredTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("account.menu.approvalRequiredDesc")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <Wine className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{t("account.dashboard.menuTitle")}</h1>
            <p className="text-sm text-muted-foreground">{t("account.menu.subtitle")}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
          {drinks.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("account.menu.empty")}</p>
          ) : (
            <div className="space-y-2">
              {drinks.map((d) => {
                const qty = drinkQty[d.id] ?? 0;
                return (
                  <div key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{tc(d.name)}</div>
                      {d.description && <div className="text-xs text-muted-foreground">{tc(d.description)}</div>}
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {d.volume_ml > 0 && <>{d.volume_ml} ml</>}
                        {d.volume_ml > 0 && d.abv_percent > 0 && " · "}
                        {d.abv_percent > 0 && <>{t("account.menu.alcohol", { percent: d.abv_percent })}</>}
                      </div>
                      <div className="mt-1 text-sm font-semibold">
                        {(d.price_kurus / 100).toLocaleString(locale)} ₺
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="icon" onClick={() => setQty(d.id, -1)} disabled={qty === 0}>
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-6 text-center font-medium">{qty}</span>
                      <Button type="button" variant="outline" size="icon" onClick={() => setQty(d.id, 1)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>




        {drinks.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("account.menu.total", { count: totalItems })}</span>
              <span className="text-2xl font-bold">{(totalKurus / 100).toLocaleString(locale)} ₺</span>
            </div>

            <div className="mt-5 rounded-xl border border-border bg-muted/20 p-3">
              <div className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <div className="font-medium">{t("account.menu.payWithJ")}</div>
                  <div className="text-xs text-muted-foreground">
                    {t("account.menu.pointsInfo", { needed: pointsNeeded, balance })}
                  </div>
                </div>
                {balance < pointsNeeded && (
                  <span className="text-xs font-medium text-destructive">{t("account.menu.insufficient")}</span>
                )}
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              disabled={submitting || totalItems === 0 || balance < pointsNeeded}
              className="mt-4 w-full"
              size="lg"
            >

              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("account.menu.payWithJ")}
            </Button>
            {balance < pointsNeeded && (
              <p className="mt-3 text-center text-xs text-muted-foreground">
                {t("account.menu.topUpHint")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
